import { format } from 'date-fns'
import type {
  InsightsHeadcountFragment,
  InsightsJoinerFragment,
  InsightsLeaverFragment,
} from 'generated/resources/graphql'
import { zip } from 'lodash'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { headcountEvolutionData } from 'modules/insights/components/WidgetHeadcountEvolution/ChartHeadcountEvolution/headcountEvolutionUtils'
import { CycleDateRange } from 'modules/insights/hooks/useCycleDateRange'

import { ExportRow, GenerateExport } from './export'

type MonthlyData = { x: string; y: number }
type HeadcountData = { id: string; data: MonthlyData[] }

export type Data = [HeadcountData, HeadcountData, HeadcountData]

export type HeadcountCurrentMetrics = {
  headcount: number
  joiners: number
  leavers: number
}

const valueOnDate = (date: Date, monthlyData: Array<{ endDate: string; value: number }>) => {
  const formattedCurrentMonth = dateToString(date, 'backend')

  return monthlyData.find((e) => e.endDate === formattedCurrentMonth)!.value
}

export default class HeadcountEvolution implements GenerateExport {
  public readonly headcountEvolution: InsightsHeadcountFragment[]
  public readonly leavers: InsightsLeaverFragment[]
  public readonly joiners: InsightsJoinerFragment[]

  private cycleDateRange: CycleDateRange

  constructor(
    headcountEvolution: InsightsHeadcountFragment[],
    leavers: InsightsLeaverFragment[],
    joiners: InsightsJoinerFragment[],
    cycleDateRange: CycleDateRange
  ) {
    this.headcountEvolution = headcountEvolution
    this.leavers = leavers
    this.joiners = joiners
    this.cycleDateRange = cycleDateRange
  }

  isEmpty() {
    const headcount = this.headcountEvolution.every((record) => record.headcount == 0)
    const leavers = this.leavers.every((record) => record.leavers == 0)
    const joiners = this.joiners.every((record) => record.joiners == 0)

    return [headcount, leavers, joiners].every((isEmpty) => isEmpty)
  }

  export() {
    const headcountReport: ExportRow[][] = [
      [
        i18n.t('insights.exports.date'),
        i18n.t('insights.widgets.shared.headcount'),
        i18n.t('insights.widgets.headcount_evolution.joiners'),
        i18n.t('insights.widgets.headcount_evolution.leavers'),
      ],
    ]
    const [headcount, joiner, leaver] = this.generateHeadcountEvolution()

    zip(headcount.data, joiner.data, leaver.data).forEach(
      ([headcountDataPoint, joinerDataPoint, leaverDataPoint]) => {
        const date = headcountDataPoint?.x ?? joinerDataPoint?.x ?? leaverDataPoint?.x

        if (date) {
          headcountReport.push([
            format(date, 'P'), // Date
            headcountDataPoint?.y ?? 0, // Headcount
            joinerDataPoint?.y ?? 0, // Joiner
            leaverDataPoint?.y ?? 0, // Leaver
          ])
        }
      }
    )

    return {
      title: i18n.t('insights.widgets.headcount_evolution.title'),
      data: headcountReport,
    }
  }

  generateHeadcountEvolution() {
    return headcountEvolutionData(
      this.headcountEvolution,
      this.leavers,
      this.joiners,
      this.cycleDateRange
    )
  }

  generateMetrics(date: Date) {
    const currentHeadcount = this.headcountOnDate(date)

    return {
      headcount: currentHeadcount,
      joiners: this.joinersOnDate(date),
      leavers: this.leaversOnDate(date),
    }
  }

  private headcountOnDate(date: Date) {
    const currentHeadcount = valueOnDate(
      date,
      this.headcountEvolution.map((item) => ({
        ...item,
        value: item.headcount,
      }))
    )

    return currentHeadcount
  }

  private joinersOnDate(date: Date) {
    const currentMonthData = valueOnDate(
      date,
      this.joiners.map((item) => ({
        ...item,
        value: item.joiners,
      }))
    )

    return currentMonthData
  }

  private leaversOnDate(date: Date) {
    const currentMonthData = valueOnDate(
      date,
      this.leavers.map((item) => ({
        ...item,
        value: item.leavers,
      }))
    )

    return currentMonthData
  }
}
